import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { isUnderManagement, type PatientListItem } from "@/api/adapters/patients";
import { modal } from "@/app/store/modal.store";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker/DatePicker";
import { Select } from "@/components/ui/Select";
import {
	scheduleAddSchema,
	toPickerDatetime,
	tomorrowDatetime,
	type ScheduleAddFormValues,
} from "@/features/patients/schemas/schedule-add.schema";
import { maskNameKeepLast } from "@/pages/medical/screens/home/utils";
import { useModalEffects } from "@/shared/hooks/use-modal-effects";
import {
	useCancelSchedule,
	useCreateSchedule,
	useEditSchedule,
} from "@/shared/hooks/use-schedule-actions";

/** An existing schedule to open (FE-13). Null/undefined = create mode. */
export interface ScheduleEditTarget {
	id: string;
	scheduledAt: string;
	type: string; // lowercase FE code: visit | inperson | call | fu
	purpose: string;
	memo: string;
	/**
	 * Past the 접근 창 — PRD 04 v2.26 §3.4 「일정 접근 창」 (SHCT-599).
	 *
	 * The window closes at 00:00 the day after the scheduled time, and from that moment the
	 * modal is 읽기 전용: 수정·취소·[중재 시작] are all unavailable. It still OPENS — the clause
	 * says so in as many words, and the row is the only place the clinician can read back what
	 * the appointment was. Refusing the click instead would hide the record rather than freeze it.
	 */
	readOnly?: boolean;
}

interface Props {
	open: boolean;
	/** Pre-fill patient (from patient header or intervention panel) */
	patient: PatientListItem | null;
	/**
	 * Candidate patients for the dropdown. Callers may pass whatever list they have — the patient page
	 * passes its currently-filtered tab — because the modal filters to schedulable (ACTIVE) patients
	 * itself (SHCT-668 / [MED.017]). The `patient` pre-fill above is exempt from that filter, so an
	 * appointment belonging to someone whose care has ended still renders its own patient.
	 */
	patients: PatientListItem[];
	/** When set, the modal opens in "edit" mode for this future schedule. */
	editTarget?: ScheduleEditTarget | null;
	/**
	 * [중재 시작] — PRD 04 §3.4 ② (SHCT-599 / MED.016). Closes this modal and opens the 신규 중재
	 * panel, prefilled from the schedule. Absent = the button is not rendered.
	 */
	onStartIntervention?: (target: ScheduleEditTarget) => void;
	onClose: () => void;
	onSaved?: (patientId: string) => void;
}

export function ScheduleAddModal({
	open,
	patient,
	patients,
	editTarget,
	onStartIntervention,
	onClose,
	onSaved,
}: Props) {
	const { t } = useTranslation(["medical", "common", "validation"]);
	// SHCT-599 — every write control reads this one flag, so 읽기 전용 cannot end up applied to
	// some of them and not the others.
	const readOnly = editTarget?.readOnly === true;
	const isEdit = !!editTarget;

	// SHCT-668 / [MED.017] — only patients who can actually be scheduled may be offered.
	//
	// The filter lives HERE rather than at the caller because the modal does not fetch its own list: it
	// is handed whatever the patient page currently has loaded, which is the CURRENTLY FILTERED tab
	// (MedicalPatientsPage `patientsData`, the flattened pages of a server-side filtered query). Standing
	// on the 종료 tab therefore offered ended patients for a new appointment. Enforcing the modal's own
	// contract here means a future second caller cannot reintroduce it, and the prop's docstring above
	// ("All active patients") finally describes what the component guarantees rather than what it hopes.
	//
	// Written as an ALLOW-LIST on ACTIVE (isUnderManagement), never a deny-list of CLOSED/WITHDRAWN: a
	// deny-list silently admits any status added later, which on a scheduling screen means quietly
	// booking appointments for a state nobody considered. patients.ts records that this exact shape has
	// already cost this codebase once.
	const schedulablePatients = patients.filter((p) => isUnderManagement(p.status));

	// The pre-filled patient is carried separately from the selectable list, and deliberately is NOT
	// subject to the filter above. An appointment that already exists for someone whose care has since
	// ended must still render its own patient: a `<select>` shows blank when its value matches no option,
	// so filtering the pre-fill away would silently empty the field of an existing record — the record
	// would then look corrupted, and saving it would be worse. Filtering decides who may be CHOSEN; it
	// must never decide what an existing appointment IS.
	//
	// Appended rather than merged into the sort so it cannot displace a schedulable patient's position,
	// and only when it is genuinely absent — a pre-filled ACTIVE patient is already in the list.
	const prefillIsSchedulable =
		!patient || schedulablePatients.some((p) => p.uid === patient.uid);
	const selectablePatients = prefillIsSchedulable
		? schedulablePatients
		: [...schedulablePatients, patient];

	// PRD §3.4: 옵션은 `이름(마스킹) (등록번호 MRN)` 표기, 이름 가나다순 정렬.
	const patientOptions = [...selectablePatients]
		.sort((a, b) => a.name.localeCompare(b.name, "ko"))
		.map((p) => ({
			value: `${p.uid}|${p.name}`,
			label: `${maskNameKeepLast(p.name)} (${p.mrn})`,
		}));

	const defaultPatientValue = patient ? `${patient.uid}|${patient.name}` : "";
	const modalRef = useRef<HTMLDivElement>(null);

	useModalEffects({ open, onClose, modalRef });

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ScheduleAddFormValues>({
		mode: "onTouched",
		resolver: zodResolver(scheduleAddSchema),
		defaultValues: {
			patientValue: defaultPatientValue,
			scheduledAt: tomorrowDatetime(),
			type: "", // MED.002: Category has no default value
			// SHCT-810 — 목적 opens empty. It used to be prefilled with
			// "후속 경과 관찰", which is a required TITLE field (PRD 04 §3.4
			// [MED.001], 1~100자) satisfying itself: the operator could save
			// without ever entering a purpose, and the unified timeline filled
			// up with the same sentence repeated. PRD 04 defines no default for
			// it, and [MED.002] already bans pre-selected defaults on the other
			// controls of this same form.
			purpose: "",
			memo: "",
		},
	});

	// Reset form on open — prefill from editTarget in edit mode.
	useEffect(() => {
		if (open) {
			reset({
				patientValue: defaultPatientValue,
				// SHCT-889 — the stored value arrives as ISO (`…T14:00`); the picker reads `Y-m-d H:i`.
				scheduledAt:
					toPickerDatetime(editTarget?.scheduledAt) ?? tomorrowDatetime(),
				type: editTarget?.type ?? "",
				purpose: editTarget?.purpose ?? "",
				memo: editTarget?.memo ?? "",
			});
		}
	}, [open, patient, editTarget, reset, defaultPatientValue]);

	const createMutate = useCreateSchedule();
	const editMutate = useEditSchedule();
	const cancelMutate = useCancelSchedule();
	const isSaving = createMutate.isPending || editMutate.isPending;

	const onSubmit = (values: ScheduleAddFormValues) => {
		const [pid] = values.patientValue.split("|");
		if (isEdit && editTarget) {
			editMutate.mutate(
				{
					id: editTarget.id,
					input: {
						scheduledAt: values.scheduledAt ?? "",
						type: (values.type ?? "").toUpperCase(),
						purpose: (values.purpose ?? "").trim(),
						memo: values.memo?.trim() || undefined,
					},
				},
				{ onSuccess: () => { onClose(); onSaved?.(pid); } },
			);
		} else {
			createMutate.mutate(
				{
					patientValue: pid,
					scheduledAt: values.scheduledAt ?? "",
					type: (values.type ?? "").toUpperCase(),
					purpose: (values.purpose ?? "").trim(),
					memo: values.memo?.trim() || undefined,
				},
				{ onSuccess: () => { onClose(); onSaved?.(pid); } },
			);
		}
	};

	// PRD §3.4 Flow C: cancel a future schedule after confirmation (not recoverable). Edit mode only.
	const handleCancelSchedule = () => {
		if (!editTarget) return;
		modal.confirm(
			t("medical:patients.schedule.cancelConfirmTitle"),
			t("medical:patients.schedule.cancelConfirmBody"),
			() => cancelMutate.mutate(editTarget.id, { onSuccess: onClose }),
		);
	};

	if (!open) return null;

	return (
		<div
			className="modal-overlay modal-overlay--visible"
			role="dialog"
			aria-modal="true"
		>
			<div className="modal">
				<div className="modal__header">
					<h3 className="modal__title">
						{t(
							isEdit
								? "medical:patients.schedule.editTitle"
								: "medical:patients.schedule.addTitle",
						)}
					</h3>
					<button
						type="button"
						className="modal__close"
						onClick={onClose}
						aria-label={t("common:actions.close")}
					>
						<X size={20} />
					</button>
				</div>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="modal__body">
						{/* 환자 */}
						<div className="form-field">
							<label className="form-field__label">
								{t("medical:patients.schedule.fields.patient")}{" "}
								<span className="required">*</span>
							</label>
							<Select
								register={register("patientValue")}
								fullWidth
								// PRD 04 §3.4: only EDIT mode locks the patient ("환자 셀렉터 비활성(고정)", line 284).
								// ADD mode from a patient context is an editable selector defaulting to the current
								// patient (lines 287-288); the option list is RBAC-scoped by the BE (Doctor own /
								// Nurse·CRC org-wide). (SHCT-583)
								disabled={isEdit || readOnly}
								options={[
									{ value: "", label: t("common:actions.select") },
									...patientOptions,
								]}
							/>
							{errors.patientValue && (
								<span className="form-field__error">
									{t(String(errors.patientValue.message))}
								</span>
							)}
						</div>

						{/* 예정 일시 */}
						<div className="form-field">
							<label className="form-field__label">
								{t("medical:patients.schedule.fields.dateTime")}{" "}
								<span className="required">*</span>
							</label>
							<DatePicker
								register={register("scheduledAt")}
								disabled={readOnly}
								enableTime
								dateFormat="Y-m-d H:i"
							/>
							{errors.scheduledAt && (
								<span className="form-field__error">
									{t(String(errors.scheduledAt.message))}
								</span>
							)}
						</div>

						{/* 유형 */}
						<div className="form-field">
							<label className="form-field__label">
								{t("medical:patients.schedule.fields.type")}{" "}
								<span className="required">*</span>
							</label>
							<Select
								register={register("type")}
								disabled={readOnly}
								fullWidth
								options={[
									{ value: "", label: t("common:actions.select") },
									...(["visit", "inperson", "call", "fu"] as const).map((v) => ({
										value: v,
										label: t(`medical:patients.schedule.types.${v}`),
									})),
								]}
							/>
							{errors.type && (
								<span className="form-field__error">
									{t(String(errors.type.message))}
								</span>
							)}
						</div>

						{/* 목적 */}
						<div className="form-field">
							<label className="form-field__label">
								{t("medical:patients.schedule.fields.purpose")}{" "}
								<span className="required">*</span>
							</label>
							<input
								type="text"
								className="input"
								maxLength={100}
								placeholder={t("medical:patients.schedule.placeholder.purpose")}
								{...register("purpose")}
								disabled={readOnly}
							/>
							{errors.purpose && (
								<span className="form-field__error">
									{t(String(errors.purpose.message))}
								</span>
							)}
						</div>

						{/* 메모 */}
						<div className="form-field">
							<label className="form-field__label">
								{t("medical:patients.schedule.fields.memo")}
							</label>
							<input
								type="text"
								className="input"
								maxLength={1000}
								placeholder={t("medical:patients.schedule.placeholder.memo")}
								{...register("memo")}
								disabled={readOnly}
							/>
							{/* SHCT-845 — 메모 is SHORT_BODY: the schema rejects emoji and >1000 chars, but the
							    message was never rendered, so the save was blocked with nothing on screen.
							    Same inline treatment as 목적 above (공통 00 §1.2 검증 시점 / §4.2 이모지 불허). */}
							{errors.memo && (
								<span className="form-field__error">
									{t(String(errors.memo.message))}
								</span>
							)}
						</div>
					</div>

					<div className="modal__footer">
						{isEdit && !readOnly && (
							<Button
								type="button"
								variant="ghost"
								className="btn--danger-text !border !border-red-500 mr-auto"
								onClick={handleCancelSchedule}
							>
								{t("medical:patients.schedule.cancelSchedule")}
							</Button>
						)}
						{/* SHCT-599 / [MED.016] — [일정 취소] 옆에 배치. 예정 일정을 실제 중재로 전환하는
						    트리거가 없어 「완료 = 링크된 중재 기록」(§4.2)이 성립하지 않던 자리다.
						    접근 창(다음 날 00:00)은 행 클릭 단계에서 한 번만 판정해 `readOnly` 로 전달한다
						    — 두 곳에서 시각을 재면 규칙이 둘로 갈린다. */}
						{isEdit && !readOnly && editTarget && onStartIntervention && (
							<Button
								type="button"
								variant="secondary"
								onClick={() => onStartIntervention(editTarget)}
							>
								{t("medical:patients.schedule.startIntervention")}
							</Button>
						)}
						<Button type="button" variant="secondary" onClick={onClose}>
							{t("common:actions.cancel")}
						</Button>
						{!readOnly && (
							<Button type="submit" variant="primary" disabled={isSaving}>
								{t("common:actions.save")}
							</Button>
						)}
					</div>
				</form>
			</div>
		</div>
	);
}
